import { useEffect } from 'react'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { formatPriceToNumber, Price, PriceRange, storeConfigSelector } from '@segway/core'
import { useAppSelector } from '@segway/core/src/store/hooks'

import { SafeAreaBottom } from '@/components'
import { Arrow, Carousel, CarouselItem, StoreIcon } from '@/components'
import { useRouter } from '@/i18n/navigation'
import { Link } from '@/i18n/navigation'
import { Button } from '@/ui'
import reportGTMEvent from '@/utils/gtm'

import { useProduct, useProductData } from '../../ProductProvider'

interface CartAwaitPopupProps {
  isHomeDelivery: boolean
  toggleCartAwaitDialog: () => void
}

const CartAwaitPopup = ({ isHomeDelivery, toggleCartAwaitDialog }: CartAwaitPopupProps) => {
  const { quantity, cartStore, selectedBundle, isEbike, responsive, totalPrice } = useProduct()
  const product = useProductData()
  const router = useRouter()
  const storeConfig = useAppSelector(storeConfigSelector)
  const t = useTranslations('Cart')

  useEffect(() => {
    router.prefetch('/checkout/cart')
  }, [router])

  /**
   * GTM: add_to_cart
   */
  useEffect(() => {
    let bundleProducts: object[] = []
    let urlKeyFullLink = ''

    if (product?.categories && product?.categories.length) {
      const urlPath = product.categories[product.categories.length - 1]?.url_path

      if (urlPath) {
        urlKeyFullLink = urlPath.replace(/\//g, '_')
      }
    }

    const item_category = product?.gtm_item_categories
      ? JSON.parse(product.gtm_item_categories)
      : {
          item_category: '',
          item_category2: '',
          item_category3: '',
          item_category4: '',
          item_category5: '',
        }

    if (selectedBundle.length) {
      bundleProducts = selectedBundle.map((item) => {
        let urlKeyFullLink = ''

        if (item?.categories && item?.categories.length) {
          const urlPath = item.categories[item.categories.length - 1]?.url_path

          if (urlPath) {
            urlKeyFullLink = urlPath.replace(/\//g, '_')
          }
        }

        const bundle_item_category = item?.gtm_item_categories
          ? JSON.parse(item.gtm_item_categories)
          : {
              item_category: '',
              item_category2: '',
              item_category3: '',
              item_category4: '',
              item_category5: '',
            }

        return {
          item_id: item.sku,
          item_name: item.name,
          item_brand: 'Segway',
          msrp: item.value,
          price: item.price,
          quantity: quantity,
          item_list_name: urlKeyFullLink,
          ...bundle_item_category,
        }
      })
    }

    reportGTMEvent({
      event: 'add_to_cart',
      ecommerce: {
        currency: product?.price_range.minimum_price.final_price.currency,
        value: formatPriceToNumber(totalPrice).numberValue,
        items: [
          {
            item_id: product?.sku,
            item_name: product?.name,
            item_brand: 'Segway',
            msrp: formatPriceToNumber(product?.price_range.minimum_price.regular_price.value || 0)
              .numberValue,
            price: formatPriceToNumber(product?.price_range.minimum_price.final_price.value || 0)
              .numberValue,
            quantity: quantity,
            item_list_name: urlKeyFullLink,
            ...item_category,
          },
          ...bundleProducts,
        ],
      },
    })
  }, [product, quantity, totalPrice, selectedBundle])

  return (
    <div className="horizontal-scrollable-container max-h-[500px] w-full overflow-y-auto px-4 xl:px-0">
      <div className="mt-0 flex flex-col xl:mt-[20px] xl:flex-row">
        <div className="border-[rgba(0, 0, 0, 0.15)] w-full flex-none xl:w-[458px] xl:border-r xl:pr-[26px]">
          <div className="flex gap-4 xl:gap-[20px]">
            {responsive && responsive.xl && (
              <div className="relative h-[160px] w-[160px] flex-none rounded-md bg-gray-1">
                <Image src={product?.small_image?.url || ''} alt="product" fill />
              </div>
            )}
            {responsive && !responsive.xl && (
              <div className="relative h-[126px] w-[126px] flex-none rounded-md bg-gray-1">
                <Image src={product?.small_image?.url || ''} alt="product" fill />
              </div>
            )}

            <div className="flex flex-1 flex-col justify-between py-1 xl:py-2">
              <p className="line-clamp-2 font-miSansSemiBold520 text-lg leading-[21px] text-gray-8">
                {product?.name}
              </p>
              <div>
                {product?.price_range && (
                  <PriceRange
                    priceRange={product.price_range}
                    priceProps={{
                      textStyle: 'xl:text-xl text-lg',
                      currencyStyle: 'xl:text-xl text-lg',
                      fractionStyle: 'xl:text-xl text-lg',
                    }}
                    originalPriceProps={{
                      textStyle: 'xl:text-xl text-lg',
                      currencyStyle: 'xl:text-xl text-lg',
                      fractionStyle: 'xl:text-xl text-lg',
                    }}
                  />
                )}
              </div>
              {storeConfig?.is_price_include_tax && (
                <div className="mt-5 flex items-center xl:mt-3">
                  <span className="font-miSansRegular330 text-[16px] text-gray-8">
                    ({t('vat_included')})
                  </span>
                </div>
              )}
              <div className="flex items-center">
                <span className="font-miSansMedium380 text-lg text-gray-8">×{quantity}</span>
              </div>
            </div>
          </div>
          {!!selectedBundle?.length &&
            selectedBundle.map((item) => (
              <div
                key={item.sku}
                className="ml-[26px] mt-3 flex gap-4 xl:ml-[32px] xl:mt-3 xl:gap-[20px]">
                {responsive && responsive.xl && (
                  <div className="relative h-[128px] w-[128px] flex-none rounded-md bg-gray-1">
                    <Image src={item?.small_image} alt="product" fill />
                  </div>
                )}
                {responsive && !responsive.xl && (
                  <div className="relative h-[100px] w-[100px] flex-none rounded-md bg-gray-1">
                    <Image src={item?.small_image} alt="product" fill />
                  </div>
                )}
                <div className="flex flex-1 flex-col justify-between py-1 xl:py-2">
                  <p className="line-clamp-2 font-miSansSemiBold520 text-lg text-gray-8 xl:leading-[21px]">
                    {item.name}
                  </p>
                  <div>
                    <Price
                      price={{ value: item.price || 0 }}
                      currencyStyle="xl:text-xl text-lg font-miSansDemiBold450"
                      fractionStyle="xl:text-xl text-lg font-miSansDemiBold450 "
                      textStyle="xl:text-xl text-lg font-miSansDemiBold450"
                    />
                  </div>
                  <div className="flex items-center">
                    <span className="font-miSansRegular330 text-[14px] text-black">
                      ×{quantity}
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </div>
        {responsive && responsive.xl && (
          <div className="ml-[26px] flex flex-1 flex-col items-center justify-start">
            <Button
              onClick={() => {
                toggleCartAwaitDialog()
                router.push('/checkout/cart')
              }}
              className="h-[48px] w-[180px] font-miSansMedium380 text-lg">
              {t('view_cart')}
            </Button>
            <div onClick={toggleCartAwaitDialog} className="mt-7 flex cursor-pointer items-center">
              <span className="mr-1 font-miSansMedium380 text-base leading-[1.4] text-gray-8">
                {t('continue_shopping')}
              </span>
              <Arrow width={16} height={16} />
            </div>
          </div>
        )}
      </div>
      {isEbike && (
        <h3 className="mt-[17px] font-miSansDemiBold450 text-lg text-[#0F0F0F] xl:mt-[32px] xl:text-2xl">
          {isHomeDelivery ? ' Your Home Delivery Store ' : 'Your Pickup Store '}
        </h3>
      )}

      {isEbike && (
        <div className="mt-4 flex items-center gap-[8px] rounded-md border border-[#E1E1E4] px-3 py-4 xl:px-4 xl:py-6">
          {responsive && responsive.xl && <StoreIcon />}
          {responsive && !responsive.xl && <StoreIcon width={16} height={16} />}
          <p className="font-miSansDemiBold450 text-base text-[#0F0F0F] xl:text-xl">
            {cartStore?.store_name}
          </p>
        </div>
      )}

      {responsive && !responsive.xl && (
        <div className="flex flex-col items-center">
          <Button
            onClick={() => {
              toggleCartAwaitDialog()
              router.push('/checkout/cart')
            }}
            className="mt-9 h-[44px] w-full font-miSansMedium380 text-lg">
            {t('view_cart')}
          </Button>
          <div
            onClick={toggleCartAwaitDialog}
            className="mb-9 mt-7 flex cursor-pointer items-center">
            <span className="font-miSansMedium380 text-base leading-[1.4] text-gray-8">
              {t('continue_shopping')}
            </span>
            <Arrow width={16} height={16} />
          </div>
        </div>
      )}

      {responsive && responsive.xl && !!product?.related_products?.length && (
        <div className="xl:pb-3">
          <h3 className="mb-4 mt-9 font-miSansDemiBold450 text-2xl text-gray-8">
            Frequently Brought With
          </h3>
          <Carousel
            autoPlay={false}
            showIndicators={false}
            isHoverShowArrows={false}
            showArrows={product.related_products.length > 4}
            className="w-full"
            carouselContentClassName="-ml-5"
            arrowIconSize={12}
            arrowLeftContainerClassName="left-auto right-[30px] h-6 w-6 -top-7"
            arrowRightContainerClassName="right-0 h-6 w-6 -top-7">
            {product?.related_products.map((item, index) => (
              <CarouselItem key={index} className={`basis-auto pl-6`}>
                <Link href={`/${item?.url_key}`}>
                  <div className="flex h-[128px] w-[128px] items-center justify-center overflow-hidden rounded-lg bg-[#F3F3F4]">
                    <Image
                      src={item?.image?.url || ''}
                      width={128}
                      height={128}
                      alt="product"
                      style={{ objectFit: 'contain' }}
                      className="aspect-square"
                    />
                  </div>
                </Link>
                <div className="mt-3 w-[128px] px-1">
                  <h3 className="mb-3 max-w-full whitespace-normal break-words font-miSansDemiBold450 text-[12px] leading-[14px]">
                    {item?.name}
                  </h3>
                  {item?.price_range && (
                    <PriceRange
                      priceRange={item.price_range}
                      priceProps={{
                        textStyle: 'text-base',
                        currencyStyle: 'text-base',
                        fractionStyle: 'text-base',
                      }}
                      originalPriceProps={{
                        textStyle: 'text-base',
                        currencyStyle: 'text-base',
                        fractionStyle: 'text-base',
                      }}
                    />
                  )}
                </div>
              </CarouselItem>
            ))}
          </Carousel>
        </div>
      )}
      {responsive && !responsive.xl && !!product?.related_products?.length && (
        <div>
          <h3 className="mb-[20px] font-miSansDemiBold450 text-xl leading-[22px] text-[#0F0F0F]">
            Frequently Brought With
          </h3>
          <Carousel
            autoPlay={false}
            showIndicators={false}
            showArrows={false}
            className="w-full"
            carouselContentClassName="-ml-3">
            {product?.related_products.map((item, index) => (
              <CarouselItem key={index} className={`basis-[50%] pl-3`}>
                <Link href={`/${item?.url_key}`}>
                  <div className="relative flex h-[165px] w-full items-center justify-center overflow-hidden rounded-[12px] bg-[#F3F3F4] xl:rounded-[20px]">
                    <Image
                      src={item?.image?.url || ''}
                      alt="product"
                      fill
                      style={{ objectFit: 'contain' }}
                    />
                  </div>
                </Link>
                <div className="mt-3">
                  <h3 className="mb-3 max-w-full font-miSansMedium380 text-base leading-[17px]">
                    {item?.name}
                  </h3>
                  {item?.price_range && (
                    <PriceRange
                      priceRange={item.price_range}
                      priceProps={{
                        textStyle: 'text-base',
                        currencyStyle: 'text-base',
                        fractionStyle: 'text-base',
                      }}
                      originalPriceProps={{
                        textStyle: 'text-base',
                        currencyStyle: 'text-base',
                        fractionStyle: 'text-base',
                      }}
                    />
                  )}
                </div>
              </CarouselItem>
            ))}
          </Carousel>
          {responsive && !responsive.xl && <SafeAreaBottom />}
        </div>
      )}
    </div>
  )
}

export default CartAwaitPopup
